/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import * as React from "react";

const inputVariants = cva(
  "flex w-full rounded-md border bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-colors",
  {
    variants: {
      variant: {
        default: "border-input focus-visible:border-primary",
        error: "border-destructive",
        success: "border-success",
      },
      inputSize: {
        sm: "h-8 text-xs px-2",
        default: "h-9 text-sm",
        lg: "h-10 px-3 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  },
);

export interface InputProps
  extends
    React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  error?: string;
  success?: string;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant,
      inputSize,
      type,
      error,
      success,
      isLoading,
      leftIcon,
      rightIcon,
      showPasswordToggle = false,
      disabled,
      "aria-describedby": ariaDescribedBy,
      id,
      style,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const autoId = React.useId();

    // Only use auto-generated ID when we actually need it (for error/success messages)
    // If no id is provided and no error/success, we don't need an ID, avoiding hydration issues
    const needsId = Boolean(id ?? error ?? success);
    const internalId = id ?? (needsId ? autoId : undefined);

    // Determine final variant based on error/success state
    const finalVariant = error ? "error" : success ? "success" : variant;

    // Handle password visibility toggle
    const finalType = type === "password" && showPassword ? "text" : type;
    const shouldShowPasswordToggle = showPasswordToggle && type === "password";

    // Build ARIA describedby
    const describedByIds = [];
    if (ariaDescribedBy) describedByIds.push(ariaDescribedBy);
    if (error && internalId) describedByIds.push(`${internalId}-error`);
    if (success && internalId) describedByIds.push(`${internalId}-success`);

    const inputElement = (
      <input
        ref={ref}
        {...(internalId ? { id: internalId } : {})}
        type={finalType}
        className={cn(
          inputVariants({ variant: finalVariant, inputSize }),
          {
            "pl-9": leftIcon,
            /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- boolean truthiness check */
            "pr-9": rightIcon || shouldShowPasswordToggle || isLoading || error,
            /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
          },
          className,
        )}
        style={style}
        disabled={disabled ?? isLoading}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={
          describedByIds.length > 0 ? describedByIds.join(" ") : undefined
        }
        {...props}
      />
    );

    return (
      <div className="relative">
        {/* Left Icon */}
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            {leftIcon}
          </div>
        )}

        {inputElement}

        {/* Right side icons/indicators */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center space-x-1 pointer-events-none">
          {/* Loading spinner */}
          {isLoading && (
            <div
              role="status"
              aria-label="Loading"
              className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-primary"
            />
          )}

          {/* Error icon */}
          {error && !isLoading && (
            <AlertCircle
              className="h-4 w-4 text-destructive"
              aria-hidden="true"
            />
          )}

          {/* Success icon */}
          {success && !isLoading && !error && (
            <div className="h-4 w-4 rounded-full flex items-center justify-center bg-success">
              <div className="h-2 w-2 rounded-full bg-success-foreground" />
            </div>
          )}

          {/* Password toggle */}
          {shouldShowPasswordToggle && !isLoading && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-muted-foreground hover:text-foreground transition-colors pointer-events-auto"
              aria-label={showPassword ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          )}

          {/* Custom right icon */}
          {rightIcon &&
            !shouldShowPasswordToggle &&
            !isLoading &&
            !error &&
            !success && (
              <div className="text-muted-foreground">{rightIcon}</div>
            )}
        </div>

        {/* Error message */}
        {error && internalId && (
          <p
            id={`${internalId}-error`}
            className="mt-1 text-sm text-destructive flex items-center gap-1"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}

        {/* Success message */}
        {success && !error && internalId && (
          <p
            id={`${internalId}-success`}
            className="mt-1 text-sm flex items-center gap-1 text-success"
            aria-live="polite"
          >
            <div className="h-3 w-3 rounded-full flex items-center justify-center flex-shrink-0 bg-success">
              <div className="h-1.5 w-1.5 rounded-full bg-success-foreground" />
            </div>
            {success}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input, inputVariants };
